import type { FC, FormEvent, ReactNode } from 'react'
import type { CartLine, FieldErrors } from '@/types'
import type { OrderField } from '@/lib/forms'

import { useState } from 'react'
import { Send } from 'lucide-react'
import { EMPTY_ORDER, validateOrder } from '@/lib/forms'
import { accentButton } from '@/components/generals/button-styles'
import {
    FormField,
    Honeypot,
    describedBy,
    inputClass
} from '@/components/generals/form-field'

interface OrderFormProps {
    lines: Array<CartLine>
    onSuccess: (result: { reference: string; total: number; email: string }) => void
}

type Status = 'idle' | 'sending' | 'error'

const OrderForm: FC<OrderFormProps> = ({ lines, onSuccess }): ReactNode => {
    const [values, setValues] = useState(EMPTY_ORDER)
    const [errors, setErrors] = useState<FieldErrors<OrderField>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    const update = (field: keyof typeof EMPTY_ORDER, value: string): void => {
        setValues(current => ({ ...current, [field]: value }))
        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const payload = { ...values, items: lines }
        const { errors: found } = validateOrder(payload)

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('error')
            setMessage(found.items ?? 'Please check the highlighted fields.')
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/orders', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            })
            const result = await response.json().catch(() => ({}))

            if (!response.ok || !result.ok) {
                setErrors(result.fields ?? {})
                setStatus('error')
                setMessage(result.error ?? "We couldn't send your order just now. Please try again in a moment.")
                return
            }

            onSuccess({ reference: result.reference, total: result.total, email: values.email })
        } catch {
            setStatus('error')
            setMessage('Your connection dropped before the order reached us. Please try again.')
        }
    }

    return (
        <form noValidate onSubmit={submit} className='relative space-y-5' aria-describedby={message ? 'order-status' : undefined}>
            <Honeypot value={values.company} onChange={value => update('company', value)} />

            <FormField id='order-name' label='Your name' error={errors.fullName}>
                <input
                    id='order-name'
                    type='text'
                    autoComplete='name'
                    value={values.fullName}
                    onChange={event => update('fullName', event.target.value)}
                    aria-invalid={Boolean(errors.fullName)}
                    aria-describedby={describedBy('order-name', errors.fullName)}
                    className={inputClass}
                />
            </FormField>

            <FormField id='order-email' label='Email' error={errors.email} hint='We send your invoice and shipping quote here.'>
                <input
                    id='order-email'
                    type='email'
                    autoComplete='email'
                    inputMode='email'
                    value={values.email}
                    onChange={event => update('email', event.target.value)}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('order-email', errors.email, 'hint')}
                    className={inputClass}
                />
            </FormField>

            <FormField id='order-country' label='Ship to country' error={errors.country}>
                <input
                    id='order-country'
                    type='text'
                    autoComplete='country-name'
                    value={values.country}
                    onChange={event => update('country', event.target.value)}
                    aria-invalid={Boolean(errors.country)}
                    aria-describedby={describedBy('order-country', errors.country)}
                    className={inputClass}
                />
            </FormField>

            <FormField id='order-notes' label='Notes' optional error={errors.notes}>
                <textarea
                    id='order-notes'
                    rows={3}
                    value={values.notes}
                    onChange={event => update('notes', event.target.value)}
                    placeholder='A colour preference, a wedding date, a gift message...'
                    className={`${inputClass} resize-y`}
                />
            </FormField>

            <button type='submit' disabled={status === 'sending'} className={`${accentButton} w-full`}>
                <Send className='size-4' strokeWidth={1.75} />
                {status === 'sending' ? 'Sending your order' : 'Send order request'}
            </button>

            <p id='order-status' role='status' className='min-h-5 text-sm text-pomegranate'>
                {status === 'error' ? message : ''}
            </p>
        </form>
    )
}

export default OrderForm
